import { useEffect, useRef, useState } from 'react'

import { cn } from '@/lib/utils'

/** Header-only height to open from: p-4 + the 44px header row + p-4. */
const COLLAPSED_PX = 76
/** Beat 2 starts just after mount, beat 3 once the 500ms growth has landed. */
const EXPAND_AT_MS = 60
const REVEAL_AT_MS = 560

function prefersReducedMotion() {
  // jsdom and some embedded contexts define `window` but not `matchMedia`.
  if (typeof window === 'undefined' || typeof window.matchMedia !== 'function') return false
  return window.matchMedia('(prefers-reduced-motion: reduce)').matches
}

/**
 * Opens a focused quote card in three beats — header, then the box growing down, then
 * the body rows fading in top to bottom — instead of appearing all at once.
 *
 * Height is released back to auto as soon as the growth finishes, so the card stays
 * free to resize when a coverage is added or a disclosure is opened.
 */
export function QuoteCardFocusReveal({ children }: { children: React.ReactNode }) {
  const innerRef = useRef<HTMLDivElement>(null)
  // Reduced motion starts on the final frame; killing transitions alone would still snap.
  const reduced = prefersReducedMotion()
  const [height, setHeight] = useState<number | undefined>(reduced ? undefined : COLLAPSED_PX)
  const [revealed, setRevealed] = useState(reduced)

  useEffect(() => {
    if (reduced) return
    const timers = [
      // Opacity doesn't affect layout, so this reads the final height while rows are hidden.
      window.setTimeout(() => {
        if (innerRef.current) setHeight(innerRef.current.offsetHeight)
      }, EXPAND_AT_MS),
      window.setTimeout(() => {
        setRevealed(true)
        setHeight(undefined)
      }, REVEAL_AT_MS),
    ]
    return () => timers.forEach(clearTimeout)
  }, [reduced])

  return (
    <div
      className={cn(
        'transition-[height] duration-500 ease-out motion-reduce:transition-none',
        height !== undefined && 'overflow-hidden'
      )}
      style={{ height }}
    >
      <div ref={innerRef} data-reveal={revealed ? 'on' : 'off'} className="quote-focus-reveal">
        {children}
      </div>
      <style>
        {`
          /* Reaches into QuoteCard's body to stagger the rows it owns. The first child is
             the header, held visible so beat 2 grows an empty card rather than a blank one. */
          /* visibility, not just opacity — aria-hidden alone leaves rows tabbable. */
          .quote-focus-reveal > div > div > *:not(:first-child) {
            transition: opacity 320ms ease-out, visibility 320ms;
          }
          /* Only the hidden state sets opacity. Declaring the revealed state as opacity:1
             outranked Tailwind's disabled:opacity-50, so disabled CTAs looked enabled. */
          .quote-focus-reveal:not([data-reveal='on']) > div > div > *:not(:first-child) {
            opacity: 0;
            visibility: hidden;
          }
          .quote-focus-reveal[data-reveal='on'] > div > div > *:nth-child(2) { transition-delay: 0ms; }
          .quote-focus-reveal[data-reveal='on'] > div > div > *:nth-child(3) { transition-delay: 100ms; }
          .quote-focus-reveal[data-reveal='on'] > div > div > *:nth-child(4) { transition-delay: 200ms; }
          .quote-focus-reveal[data-reveal='on'] > div > div > *:nth-child(5) { transition-delay: 300ms; }
          .quote-focus-reveal[data-reveal='on'] > div > div > *:nth-child(6) { transition-delay: 400ms; }
          .quote-focus-reveal[data-reveal='on'] > div > div > *:nth-child(n + 7) { transition-delay: 500ms; }

          @media (prefers-reduced-motion: reduce) {
            .quote-focus-reveal > div > div > *:not(:first-child) { transition: none; }
          }
        `}
      </style>
    </div>
  )
}
